/**
 * Phase 10 — Super-admin auth.
 *
 * Mirrors the Phase 9 client-ui AuthProvider, but:
 *   - Uses bcrypt login against `POST /admin/auth/login` (email+password).
 *   - Stores the admin JWT in memory only.
 *   - Sends it via the `X-Admin-Authorization` header (per Phase 2 middleware
 *     priority — the tenant `Authorization` header is intentionally
 *     reserved for tenant principals).
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface AdminAuthState {
  jwt: string | null;
  email: string | null;
}

export interface AdminAuthContextValue extends AdminAuthState {
  login(email: string, password: string): Promise<void>;
  logout(): void;
  fetcher<T = unknown>(path: string, init?: RequestInit): Promise<T>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export class ApiError extends Error {
  status: number;
  body: unknown;
  code: string | undefined;
  retryAfterMs: number | undefined;
  constructor(status: number, body: unknown) {
    super(extractMessage(body) ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
    const b = (body ?? {}) as Record<string, unknown>;
    this.code = typeof b['error'] === 'string' ? (b['error'] as string) : undefined;
    this.retryAfterMs =
      typeof b['retry_after_ms'] === 'number'
        ? (b['retry_after_ms'] as number)
        : undefined;
  }
}

function extractMessage(body: unknown): string | undefined {
  if (body && typeof body === 'object') {
    const m = (body as Record<string, unknown>)['message'];
    if (typeof m === 'string') return m;
  }
  return undefined;
}

async function safeJson(resp: Response): Promise<unknown> {
  try {
    return await resp.json();
  } catch {
    return undefined;
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AdminAuthState>({ jwt: null, email: null });

  const login = useCallback(async (email: string, password: string) => {
    const resp = await fetch('/admin/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!resp.ok) {
      throw new ApiError(resp.status, await safeJson(resp));
    }
    const body = (await resp.json()) as { token: string };
    setState({ jwt: body.token, email });
  }, []);

  const logout = useCallback(() => {
    setState({ jwt: null, email: null });
    // G3 — clear cached admin data (tenant PII) on logout.
    queryClient.clear();
  }, [queryClient]);

  const fetcher = useCallback(
    async <T,>(path: string, init?: RequestInit): Promise<T> => {
      const headers = new Headers(init?.headers);
      if (state.jwt) {
        headers.set('X-Admin-Authorization', `Bearer ${state.jwt}`);
      }
      if (init?.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      const resp = await fetch(path, { ...init, headers });
      if (resp.status === 204) return undefined as T;
      const body = await safeJson(resp);
      if (!resp.ok) {
        if (resp.status === 401) {
          setState({ jwt: null, email: null });
        }
        throw new ApiError(resp.status, body);
      }
      return body as T;
    },
    [state.jwt],
  );

  const value = useMemo<AdminAuthContextValue>(
    () => ({ ...state, login, logout, fetcher }),
    [state, login, logout, fetcher],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth() must be inside <AdminAuthProvider>');
  return ctx;
}
